import * as Icons from "@nova/component-icons"
import { SidePanelLayerOrGroup, SidePanelLayerType } from "@nova/constants"
import { Group } from "@nova/protobuf"
import {
    createFractionalIndexBetween,
    END,
} from "@nova/serializer/src/client/util/fractional_indexing"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import {
    selectGroupById,
    selectGroups,
    selectPDFGroups,
} from "features/entity-caching/groups/selectors"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { deleteGroup, updateGroup } from "features/pubsub/entityActions/cubes/groups"
import { deletePDFJob } from "features/pubsub/entityActions/jobs/pdf"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { dataSelectorsByMode, SidePanelMode } from "panels/content/side-panel/constants"
import { useMoveActions } from "panels/content/side-panel/generics/genericContextMenuSections"
import { getGenericLayerListItem } from "panels/content/side-panel/generics/GenericLayerListItem"
import { getUseSidePanelListItemVisibilityAndLockActions } from "panels/content/side-panel/generics/genericListItemActions"
import { getNestedLayerCount } from "panels/content/side-panel/generics/genericMultipleSelectedContextMenuActions"
import { getUseSidePanelEntities } from "panels/content/side-panel/generics/genericUseSidePanelEntities"
import { useArchiveContextMenuOption } from "panels/content/side-panel/generics/useArchiveContextMenuOption"
import { selectAllEntitiesMap, selectEntityById } from "panels/content/side-panel/slice"
import { setRenamingLayerId } from "panels/content/side-panel/slice.new"
import { selectPDFJobs } from "plugins/PDFv2/PDFSlice"
import {
    PluginRegistry,
    PluginSpec,
    getSidePanelLayerTypeToDeleteFunctionMap,
    getSidePanelLayerTypeToUpdateFunctionMap,
} from "plugins/plugin-registry"
import { useCallback } from "react"
import { useIsEditor } from "tools/hooks/useIsEditor"

export function registerGroupPlugin(registry: PluginRegistry) {
    registry
        .register<Group>({
            name: "groups",
            displayName: "Group",
            sidePanelLayerType: SidePanelLayerType.Group,
            PrimaryIcon: Icons.Group,
            SecondaryIcon: Icons.GroupOpen,
            updateAction: updateGroup,
            deleteAction: deleteGroup,
            confirmDelete: () => true,
            protobufMessage: Group,
            selectSingleEntity: selectGroupById,
            selectAllEntities: selectGroups,
            zoomTo: () => undefined,
            duplicate: () => undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            useSidePanelEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectGroups,
            }),
            filterKey: undefined, // Show groups no matter what layers filter is applied
            SidePanelListItem: getGenericLayerListItem({
                spec,
                useActions: getUseSidePanelListItemVisibilityAndLockActions(spec),
            }),
            useContextMenuSections: getUseGroupLayerContextMenuSections(spec),
        }))
        .implement("side-panel-pdf-plugin", (spec) => ({
            useSidePanelEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectPDFGroups,
            }),
            SidePanelListItem: getGenericLayerListItem({
                spec,
                useActions: () => [],
            }),
            useContextMenuSections: getUseGroupPdfContextMenuSections(spec),
        }))
}

function useUngroup({
    id,
    mode,
    sortedEntities,
}: {
    id: string
    mode: SidePanelMode
    sortedEntities: SidePanelLayerOrGroup[]
}) {
    const dispatch = useAppDispatch()
    const entityMap = useAppSelector(dataSelectorsByMode[mode].selectEntityMap)
    const handleUngroup = useCallback(() => {
        const group = entityMap[id]
        if (!group) {
            console.error(`No group for ID ${id}`)
            return
        }
        let parent: SidePanelLayerOrGroup | undefined = undefined
        if (entityMap[id].parentId && entityMap[id].parentId !== "null") {
            parent = entityMap[entityMap[id].parentId]
        }
        const newSiblings = parent?.children ? parent.children : sortedEntities
        const numericalIndexInSiblings = newSiblings?.findIndex((sibling) => sibling.id === id)
        const end =
            numericalIndexInSiblings === newSiblings.length - 1
                ? END
                : newSiblings[numericalIndexInSiblings + 1].index
        dispatch(deleteGroup({ id: group.id }))
        if (!group?.children?.length) return
        if (group.children[0].type) {
            dispatch(
                getSidePanelLayerTypeToUpdateFunctionMap()[group.children[0].type]({
                    id: group.children[0].id,
                    updates: {
                        parentId: group.parentId,
                        index: group.index,
                    },
                })
            )
        }
        let prevIndex = group.index
        group.children.forEach((child, i) => {
            if (i === 0) {
                // skip the first child
                return
            }
            const newIndex = createFractionalIndexBetween(prevIndex, end)
            if (child.type) {
                dispatch(
                    getSidePanelLayerTypeToUpdateFunctionMap()[child.type]({
                        id: child.id,
                        updates: {
                            parentId: group.parentId,
                            index: newIndex,
                        },
                    })
                )
            }
            prevIndex = newIndex
        })
    }, [dispatch, entityMap, id, sortedEntities])
    return handleUngroup
}

function getUseGroupLayerContextMenuSections(spec: PluginSpec<Group>) {
    const useContextMenuSections = (id: string, sortedEntities: SidePanelLayerOrGroup[]) => {
        const dispatch = useAppDispatch()
        const entity = useAppSelector(selectEntityById(id))
        const confirmationModal = useConfirmationModalContext()
        const handleUngroup = useUngroup({
            id,
            mode: "Layers",
            sortedEntities,
        })
        const isEditor = useIsEditor()
        const actions: ContextMenuItem[][] = []
        if (isEditor) {
            actions.push([
                {
                    title: "Rename",
                    onClick: () => dispatch(setRenamingLayerId({ id })),
                    Icon: Icons.Pencil,
                },
            ])
        }
        if (isEditor) {
            actions.push([
                {
                    title: "Ungroup",
                    onClick: handleUngroup,
                    Icon: Icons.Folder,
                },
            ])
        }
        if (isEditor) {
            const mapActions: ContextMenuItem[] = []
            if (entity.visible) {
                mapActions.push({
                    title: "Hide",
                    onClick: () => {
                        dispatch(updateGroup({ id, updates: { visible: false } }))
                    },
                    Icon: Icons.VisibilityEyeOff,
                })
            } else {
                mapActions.push({
                    title: "Show",
                    onClick: () => {
                        dispatch(updateGroup({ id, updates: { visible: true } }))
                    },
                    Icon: Icons.VisibilityEye,
                })
            }
            if (entity.locked) {
                mapActions.push({
                    title: "Unlock",
                    onClick: () => {
                        dispatch(updateGroup({ id, updates: { locked: false } }))
                    },
                    Icon: Icons.LayerUnlocked,
                })
            } else {
                mapActions.push({
                    title: "Lock",
                    onClick: () => {
                        dispatch(updateGroup({ id, updates: { locked: true } }))
                    },
                    Icon: Icons.LayerLocked,
                })
            }
            actions.push(mapActions)
        }
        const moveActions = useMoveActions({
            spec,
            id,
            mode: "Layers",
            sortedEntities,
        })
        if (moveActions.length) {
            actions.push(moveActions)
        }
        const archiveAction = useArchiveContextMenuOption(id)
        if (archiveAction) {
            actions.push([archiveAction])
        }
        if (isEditor) {
            actions.push([
                {
                    title: `Delete ${1 + getNestedLayerCount(entity.children || [])} Layers`,
                    onClick: async () => {
                        const didConfirm = await confirmationModal.confirm({
                            RenderComponent: getGenericConfirmationModalComponent({
                                title: "Delete Layers?",
                                message: `This will delete ${1 + getNestedLayerCount(entity.children || [])} layers, do you want to proceed?`,
                            }),
                        })
                        if (!didConfirm) return
                        const state = store.getState()
                        const entityMap = selectAllEntitiesMap(state)
                        const entityIdsToDelete = [id]
                        while (entityIdsToDelete.length) {
                            const curId = entityIdsToDelete.pop()!
                            const curEntity = entityMap[curId]
                            if (curEntity) {
                                if (curEntity.children) {
                                    entityIdsToDelete.push(
                                        ...curEntity.children.map((child) => child.id)
                                    )
                                }
                                if (curEntity.type) {
                                    dispatch(
                                        getSidePanelLayerTypeToDeleteFunctionMap()[curEntity.type]({
                                            id: curId,
                                        })
                                    )
                                }
                            }
                        }
                    },
                    Icon: Icons.Trash,
                    isDestructive: true,
                },
            ])
        }
        return actions
    }
    return useContextMenuSections
}

function getUseGroupPdfContextMenuSections(spec: PluginSpec<Group>) {
    const useContextMenuSections = (id: string, sortedEntities: SidePanelLayerOrGroup[]) => {
        const dispatch = useAppDispatch()
        const pdfJobs = useAppSelector(selectPDFJobs)
        const confirmationModal = useConfirmationModalContext()
        const handleUngroup = useUngroup({
            id,
            mode: "PDFs",
            sortedEntities,
        })
        const isEditor = useIsEditor()
        const actions: ContextMenuItem[][] = []
        if (isEditor) {
            actions.push([
                {
                    title: "Ungroup",
                    onClick: handleUngroup,
                    Icon: Icons.Folder,
                },
            ])
        }
        const moveActions = useMoveActions({
            spec,
            id,
            mode: "PDFs",
            sortedEntities,
        })
        if (moveActions.length) {
            actions.push(moveActions)
        }
        if (isEditor) {
            actions.push([
                {
                    title: `Delete ${1 + pdfJobs.filter((job) => job.parentId === id).length} Layers`,
                    onClick: async () => {
                        const didConfirm = await confirmationModal.confirm({
                            RenderComponent: getGenericConfirmationModalComponent({
                                title: "Delete Layers?",
                                message: `This will delete ${1 + pdfJobs.filter((job) => job.parentId === id).length} layers, do you want to proceed?`,
                            }),
                        })
                        if (!didConfirm) return
                        dispatch(deleteGroup({ id }))
                        pdfJobs
                            .filter((job) => job.parentId === id)
                            .forEach((job) => dispatch(deletePDFJob({ id: job.id })))
                    },
                    Icon: Icons.Trash,
                    isDestructive: true,
                },
            ])
        }
        return actions
    }
    return useContextMenuSections
}
